import { useContext, useEffect, useState } from 'react';

import {
  useLazyGetMedicalDocumentXmlQuery,
  useSignMedicalDocumentMutation,
} from '@app/api/MedicalDocuments';
import { SUCCESS } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import {
  CryptoProError,
  CryptoProErrorCode,
  getCertificates,
  isPluginAvailable,
  signContent,
} from '@app/shared/cryptoPro';
import type { CryptoProCertificate } from '@app/shared/cryptoPro';

import type { SignDocumentModalProps } from '../types';

// Последний использованный сертификат: у подписанта он один и тот же,
// повторный выбор в списке не нужен.
const LAST_CERTIFICATE_THUMBPRINT_KEY = 'cryptoProLastCertificateThumbprint';

export const useSignDocument = ({
  documentId,
  role,
  onClose,
  onSigned,
}: SignDocumentModalProps) => {
  const [certificates, setCertificates] = useState<CryptoProCertificate[]>([]);
  const [selectedThumbprint, setSelectedThumbprint] = useState<string | null>(
    null
  );
  const [isPreparing, setPreparing] = useState(false);
  const [isPluginMissing, setPluginMissing] = useState(false);
  const [isSigning, setSigning] = useState(false);

  const { activeBranchId: ActiveBranchId } = useUserPrint();
  const { showNotification } = useContext(NotificationContext);

  const [getMedicalDocumentXml] = useLazyGetMedicalDocumentXmlQuery();
  const [signMedicalDocument] = useSignMedicalDocumentMutation();

  const isOpen = !!documentId && !!role;

  useEffect(() => {
    if (!isOpen) return undefined;

    let isCancelled = false;

    const prepare = async () => {
      setPreparing(true);
      setPluginMissing(false);
      setCertificates([]);
      setSelectedThumbprint(null);

      try {
        const isAvailable = await isPluginAvailable();

        if (isCancelled) return;

        if (!isAvailable) {
          setPluginMissing(true);

          return;
        }

        const availableCertificates = await getCertificates();

        if (isCancelled) return;

        setCertificates(availableCertificates);

        const lastUsedThumbprint = localStorage.getItem(
          LAST_CERTIFICATE_THUMBPRINT_KEY
        );
        const preselected =
          availableCertificates.find(
            (certificate) => certificate.thumbprint === lastUsedThumbprint
          ) ?? availableCertificates[0];

        setSelectedThumbprint(preselected?.thumbprint ?? null);
      } catch (error) {
        if (isCancelled) return;

        showNotification(
          error instanceof CryptoProError
            ? error.message
            : handleCatchError(error)
        );
      } finally {
        if (!isCancelled) setPreparing(false);
      }
    };

    prepare();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, showNotification]);

  const handleSign = async () => {
    // Без полного набора идентификаторов запросы не отправляем.
    if (!documentId || !role || !selectedThumbprint || !ActiveBranchId) return;

    setSigning(true);

    try {
      // Документ приходит в Base64 и подписывается целиком: хэш считает сам
      // плагин алгоритмом из ключа сертификата (ГОСТ), на бэке хэширования нет.
      const documentXml = await getMedicalDocumentXml({
        ActiveBranchId,
        id: documentId,
      }).unwrap();

      if (!documentXml || !documentXml.trim()) {
        showNotification(
          'Бэкенд не вернул документ для подписания (GetDocumentXml) — подписание невозможно'
        );

        return;
      }

      const signatureData = await signContent(selectedThumbprint, documentXml);

      const updatedDocument = await signMedicalDocument({
        ActiveBranchId,
        documentId,
        data: {
          Role: role,
          CertificateThumbprint: selectedThumbprint,
          SignatureData: signatureData,
        },
      }).unwrap();

      localStorage.setItem(LAST_CERTIFICATE_THUMBPRINT_KEY, selectedThumbprint);

      showNotification(
        role === 'CHIEF'
          ? 'Документ подписан за организацию'
          : 'Документ подписан врачом',
        SUCCESS
      );

      onSigned(updatedDocument);
      onClose();
    } catch (error) {
      if (
        error instanceof CryptoProError &&
        error.code === CryptoProErrorCode.UserCancelled
      ) {
        // Отмена в окне плагина — не ошибка: остаёмся в модалке.
        return;
      }

      showNotification(
        error instanceof CryptoProError
          ? error.message
          : handleCatchError(error)
      );
    } finally {
      setSigning(false);
    }
  };

  return {
    isOpen,
    isPreparing,
    isPluginMissing,
    isSigning,
    certificates,
    selectedThumbprint,
    setSelectedThumbprint,
    handleSign,
  };
};
